import React from "react";
import { DownloadJsonIcon } from "./DownloadJsonIcon";

const template = {
  caseData: {
    data: {
      agentApiApplication: {
        applicant: {
          employment: {
            type: true,
            contractType: true,
            pensionType: true,
            fromYear: true,
            professionType: true,
            companyName: true,
            startDate: true,
          },
          housing: true,
          maritalStatus: true,
          gender: true,
          birthDate: true,
          addressInformation: true,
          citizenships: true,
          expenses: true,
          income: true,
          psD2Data: {
            accounts: true,
            oldestTransaction: true,
            transactions: true,
          },
          numberOfExistingLoans: true,
          paymentsPerYear: true,
          firstName: true,
          lastName: true,
          nationalId: true,
          mobileNumber: true,
          emailAddress: true,
          numberOfChildrenBelow18: true,
          educationLevel: true,
        },
      },
      ipAddress: true,
      integrations: {
        LeaBankWeb: {
          confirmAccountNumber: true,
          PrivacyConsent: true,
          DataUsageConsent: true,
          MarketingConsent: true,
          ThirdPartyShareConsent: true,
        },
        electronicIdVideoId: {
          getVerificationStatus: {
            primaryName: true,
            secondaryName: true,
            sex: true,
            nationality: true,
            personalNumber: true,
            documentNumber: true,
          },
        },
      },
    },
  },
};

const applyTemplate = (source: any, template: any): any => {
  if (template === true) return source; // Include the full subtree/field

  // If the template is not an object or source is invalid, return undefined
  if (typeof template !== "object" || template === null) return undefined;
  if (typeof source !== "object" || source === null) return undefined;

  // Traverse each key in the template and apply recursively
  return Object.entries(template).reduce(
    (result, [key, subTemplate]) => {
      if (Object.hasOwn(source, key)) {
        const filtered = applyTemplate(source[key], subTemplate);
        if (filtered !== undefined) {
          (result as Record<string, unknown>)[key] = filtered; // Include key if value is not undefined
        }
      }
      return result;
    },
    Array.isArray(source) ? [] : {},
  ); // Maintain array structure if applicable
};

export const DownloadJsonButton = (props: { flow: any }) => {
  const { flow } = props;
  return (
    <span
      title="Applicant GDPR data"
      style={{ cursor: "pointer" }}
      onClick={() => {
        // download contents of flow object as JSON file
        const clonedFlow = { caseData: structuredClone(flow.caseData) };
        const filteredData = applyTemplate(clonedFlow, template);

        // strip transactions to first and last item
        const psD2Data =
          filteredData?.caseData?.data?.agentApiApplication?.applicant
            ?.psD2Data;

        if (psD2Data) {
          const accounts = psD2Data.accounts ?? [];
          const tx = psD2Data.transactions ?? [];

          // get first and last tx for each account
          if (accounts.length > 0) {
            const filteredTx = accounts.flatMap((account: any) => {
              const accountTx = tx
                .filter((t: any) => t.accountId === account.accountId)
                .sort(
                  (a: any, b: any) =>
                    new Date(a.date).getTime() - new Date(b.date).getTime(),
                );
              return accountTx.length > 2
                ? [accountTx[0], accountTx.at(-1)]
                : accountTx
            });
            psD2Data.transactions = filteredTx;
          }
        }

        const dataStr = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(filteredData, null, 2))}`;
        const downloadAnchorNode = document.createElement("a");
        downloadAnchorNode.setAttribute("href", dataStr);
        downloadAnchorNode.setAttribute(
          "download",
          `${flow.caseData.referenceId || "application"}-data.json`,
        );
        document.body.appendChild(downloadAnchorNode); // required for firefox
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
      }}
    >
      <DownloadJsonIcon />
    </span>
  );
};
